import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, onUnauthorized } from '../api';

const AuthContext = createContext(null);
const TOKEN_KEY   = 'eo_token';

export function AuthProvider({ children }) {
  const [token,    setToken]    = useState(() => localStorage.getItem(TOKEN_KEY));
  const [business, setBusiness] = useState(null);
  const [loading,  setLoading]  = useState(true);

  // Track whether we're mid-login so a stale getMe 401 doesn't kick us out
  const loggingIn = useRef(false);

  // ── Initial load: verify stored token and load business ──────────────────
  const loadBusiness = useCallback(async () => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      const biz = await getMe(stored);
      setBusiness(biz);
      setToken(stored);
    } catch {
      // Only clear if the token hasn't been replaced by a fresh login
      // (saveSession may have stored a new token while this check was in-flight)
      if (localStorage.getItem(TOKEN_KEY) === stored) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setBusiness(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBusiness(); }, [loadBusiness]);

  // ── Subscribe to 401 events from api.js (for post-login API calls) ────────
  useEffect(() => {
    const unsub = onUnauthorized(() => {
      // Only log out if we are NOT in the middle of a login/register attempt
      if (!loggingIn.current) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setBusiness(null);
      }
    });
    return unsub;
  }, []);

  // ── Called after a successful register or login ───────────────────────────
  const saveSession = useCallback((newToken, bizData) => {
    loggingIn.current = true;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setBusiness(bizData);
    // Release the guard after React flushes state
    setTimeout(() => { loggingIn.current = false; }, 10000);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBusiness(null);
  }, []);

  const refreshBusiness = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    try {
      setBusiness(await getMe(t));
    } catch {
      logout();
    }
  }, [logout]);

  return (
    <AuthContext.Provider value={{ token, business, loading, saveSession, logout, refreshBusiness }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
